import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useState } from 'react';
import { Eye } from '@phosphor-icons/react';

import {
  Form,
  Spacer,
  TextField,
  InternalPage,
  IconButton,
} from 'theSource/components';
import { RequiredLegend } from 'enrollment/features/Shared/RequiredLegend/RequiredLegend';
import { TypographyStyled } from './ChangePasswordFormStyled';
import {
  CHANGE_PASSWORD_INITIAL_VALUES,
  CHANGE_PASSWORD_QUERY_KEY,
} from './utils';
import { useApiMutation, newPasswordIsValid } from 'theSource/utils';
import {
  ChangePasswordErrorDto,
  ChangePasswordFormValues,
  ChangePasswordRequestDto,
  ChangePasswordResponseDto,
} from './types';
import {
  GET_CONTENT_SETTINGS_QUERY_KEY,
  usePasswordRequirementsQuery,
  useConfigurationQuery,
  useCompanyManifestQuery,
} from 'theSource/queries';
import { ROUTES } from 'theSource/app/routes';
import {
  selectUserName,
  selectToken,
} from 'theSource/features/ForgotPassword/services';
import { PasswordRequirements } from 'theSource/features/PasswordRequirements';
import { FormValues } from 'theSource/types';

export const LumosChangePasswordForm = () => {
  const stateUsername = useSelector(selectUserName);
  const params = new URLSearchParams(window.location.search);
  const paramsUsername = params.get('username');
  const userName = stateUsername || paramsUsername;
  const getTokenFromQuery = () => {
    return params.get('token');
  };
  let token = useSelector(selectToken);

  if (!token) {
    token = getTokenFromQuery();
  }

  const { t } = useTranslation('pages.forgotPassword');

  const navigate = useNavigate();

  const { configuration } = useConfigurationQuery();
  const baseUrl = configuration?.apiHost;

  const { data: manifestData, isLoading: isLoadingManifestData } =
    useCompanyManifestQuery({ enabled: Boolean(configuration?.apiHost) });

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const {
    requirements,
    isLoadingPasswordRequirements,
    isFetchingPasswordRequirements,
  } = usePasswordRequirementsQuery(
    newPassword,
    userName || '',
    manifestData?.organization_id,
    baseUrl
  );

  const isLoadingRequirements =
    isLoadingManifestData ||
    isLoadingPasswordRequirements ||
    isFetchingPasswordRequirements;

  const passwordChange = (values: FormValues) => {
    const newPassword = values.newPassword as string;
    const confirmPassword = values.confirmPassword as string;
    setNewPassword(newPassword);
    setConfirmPassword(confirmPassword);
  };

  const newPasswordValid = newPasswordIsValid(newPassword, requirements);
  const formValid = newPasswordValid && newPassword === confirmPassword;

  const { isLoading, mutate: changePassword } = useApiMutation<
    ChangePasswordResponseDto,
    ChangePasswordErrorDto,
    ChangePasswordRequestDto
  >({
    queryKey: CHANGE_PASSWORD_QUERY_KEY,
    method: 'put',
    endpoint: '/update_password',
    queryKeysToInvalidate: GET_CONTENT_SETTINGS_QUERY_KEY,
    getErrorMessage: (error) => error.response?.data.details,
    getSuccessMessage: () => t('passwordSuccessfullyChanged'),
    snackbarOptions: {
      position: 'bottom',
    },
    onSuccess: () => navigate(ROUTES.public.login.path),
  });

  const [displayNewPassword, setDisplayNewPassword] = useState(false);
  const [displayConfirmPassword, setDisplayConfirmPassword] = useState(false);

  const handleDisplayNewPassword = () => {
    setDisplayNewPassword(!displayNewPassword);
  };

  const handleDisplayConfirmPassword = () => {
    setDisplayConfirmPassword(!displayConfirmPassword);
  };

  const handleSubmit = (values: ChangePasswordFormValues) =>
    changePassword({
      token: token,
      password: values.newPassword,
      confirm_password: values.confirmPassword,
    });

  return (
    <InternalPage
      title={t('changePasswordTitle')}
      description={t('changePasswordDescription')}
      withBackLink
    >
      <Form
        onSubmitClick={handleSubmit}
        initialValues={CHANGE_PASSWORD_INITIAL_VALUES}
        isLoading={isLoading}
        enableDirtyFormHandling={false}
        submitButtonLabel={t('changePassword')}
        submitButtonUseFullWidth
        testId="change-password-form"
        onChange={passwordChange}
        disableSubmitButton={!formValid}
      >
        <Box>
          <TypographyStyled variant="body1">
            {t('changingPasswordFor')}
          </TypographyStyled>
          <h2 className="headline-3">{userName}</h2>
        </Box>
        <div className="mt-2">
          <RequiredLegend />
        </div>
        <TextField
          fullWidth
          required
          type={displayNewPassword ? 'text' : 'password'}
          id="newPassword"
          name="newPassword"
          label={t('newPassword')}
          endAdornment={
            <IconButton
              Icon={Eye}
              size={3.0}
              aria-label="toggle-display-new-password"
              onClick={handleDisplayNewPassword}
              variant="tertiary"
            />
          }
        />
        <PasswordRequirements
          password={newPassword}
          loadingRequirements={isLoadingRequirements}
          requirements={requirements}
        />
        <TextField
          fullWidth
          required
          type={displayConfirmPassword ? 'text' : 'password'}
          id="confirmPassword"
          name="confirmPassword"
          label={t('confirmPassword')}
          disabled={!newPasswordValid}
          endAdornment={
            <IconButton
              Icon={Eye}
              size={3.3}
              aria-label="toggle-display-confirm-password"
              onClick={handleDisplayConfirmPassword}
              variant="tertiary"
              disabled={!newPasswordValid}
            />
          }
        />
        <Spacer />
      </Form>
    </InternalPage>
  );
};
